// /components/:widgetId/hosted (hosted widgets only): the company details shown in
// the hosted page's header and footer (saved with the header's Save), a preview
// of that header and footer, the page's link and a QR code that opens it.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import QRCode from "qrcode";
import { Check, Copy, Download, ExternalLink, UserRound } from "lucide-react";
import {
  EMPTY_HOSTED,
  createMockApi,
  ensureGoogleFont,
  fontStack,
  hostedTheme,
  mountWidget,
  normaliseBranding,
  normalisePricing,
  type HostedDetails,
  type WidgetBranding,
  type WidgetHandle,
  type WidgetPricing,
  type WidgetProduct,
} from "@widget";
import { useApi } from "../../api";
import { solarPreviewBackend } from "../../solarPreview";
import { ColorField, FontPicker, Toggle } from "../../components/controls";
import { HostedLinkEditor } from "../../components/HostedLinkEditor";
import { LogoUpload } from "../../components/LogoUpload";
import { useSaveBar, useTabDraft, useWidget } from "./WidgetLayout";

// The form: field, label, hint, input type
type TextField = Exclude<keyof HostedDetails, "logoUrl" | "theme">;
const FIELDS: [TextField, string, string, string][] = [
  ["companyName", "Company name", "Shown in the header and footer.", "text"],
  ["tagline", "Tagline", "A short line under your name, e.g. “MCS certified heat pump installers”.", "text"],
  ["phone", "Phone", "Shown as a call button in the header.", "tel"],
  ["email", "Email", "Shown as an email button in the header.", "email"],
  ["website", "Website", "Starts with https://", "url"],
  ["address", "Address", "One line, shown in the footer.", "text"],
  ["companyNumber", "Company number", "", "text"],
  ["vatNumber", "VAT number", "", "text"],
  ["privacyUrl", "Privacy policy link", "Starts with https://", "url"],
];

export function HostedPage() {
  const { widget, setWidget } = useWidget();
  const api = useApi();
  const saved = useMemo(() => ({ ...EMPTY_HOSTED, ...(widget.hosted ?? {}) }), [widget.hosted]);
  // Kept by the layout, so unsaved edits survive switching tabs
  const [draft, setDraft, discardDraft] = useTabDraft<HostedDetails>("hosted", saved);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  const [qrPng, setQrPng] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [filling, setFilling] = useState(false);
  const url = widget.hostedUrl ?? null;
  const branding = normaliseBranding(widget.branding);


  // The QR code opens the hosted page (high error correction: survives print and logos nearby)
  useEffect(() => {
    if (!url) return;
    QRCode.toDataURL(url, { width: 640, margin: 2, errorCorrectionLevel: "H", color: { dark: "#000000", light: "#FFFFFF" } })
      .then(setQrPng)
      .catch(() => setQrPng(null));
  }, [url]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  useSaveBar({
    dirty,
    saving,
    blocked: false,
    onSave: save,
    onDiscard: () => {
      setDraft(saved);
      setMessage(null);
    },
  });

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      setWidget(await api.updateWidget(widget.widgetId, { hosted: draft }));
      discardDraft();
      setMessage({ tone: "success", text: "Hosted page details saved." });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function downloadSvg() {
    if (!url) return;
    const svg = await QRCode.toString(url, { type: "svg", margin: 2, errorCorrectionLevel: "H" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
    a.download = `quote-page-${widget.hostedCode}.svg`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  // Fill blank header/footer fields from the company profile (Account → Profile)
  async function fillFromProfile() {
    setFilling(true);
    setMessage(null);
    try {
      const { company } = await api.getProfile();
      const blanks = FIELDS.filter(([key]) => !String(draft[key] ?? "").trim() && company[key]);
      if (blanks.length === 0) {
        setMessage({ tone: "success", text: "Nothing to fill: every field with a value in your profile is already filled in here." });
        return;
      }
      setDraft({ ...draft, ...Object.fromEntries(blanks.map(([key]) => [key, company[key]])) });
      setMessage({ tone: "success", text: `Filled ${blanks.length} field${blanks.length === 1 ? "" : "s"} from your profile. Save to keep them.` });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setFilling(false);
    }
  }

  function copy() {
    if (!url) return;
    void navigator.clipboard?.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    });
  }

  const d = draft;
  return (
    <div className="hosted">
      {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}

      {/* Link + QR code */}
      <section className="card hosted-link">
        <div>
          <h2>Your quote page</h2>
          <p className="card-sub">Send customers here, or print the QR code on flyers, vans and letters. The page uses this calculator's branding and prices.</p>
          {url ? (
            <>
              <code className="site-key">{url}</code>
              {/* Optional, once: swap the generated code for the installer's own */}
              <HostedLinkEditor widget={widget} onSaved={setWidget} />
              <div className="hosted-actions">
                <button type="button" className="btn btn-secondary" onClick={copy}>
                  {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />} {copied ? "Copied" : "Copy link"}
                </button>
                {/* Cache-buster so the installer always sees their latest saved version (the
                    page ignores the query; the copied link and QR code stay clean) */}
                <a className="btn btn-secondary" href={`${url}?v=${Date.now()}`} target="_blank" rel="noreferrer">
                  <ExternalLink size={16} aria-hidden="true" /> Open the page
                </a>
              </div>
              {widget.statusMissing.length > 0 && (
                <p className="price-error">The page shows the calculator once it is set up: {widget.statusMissing.map((m) => m.text.toLowerCase()).join(", ")}.</p>
              )}
            </>
          ) : (
            <p className="muted">Save this page once to get its link.</p>
          )}
        </div>
        {url && qrPng && (
          <div className="hosted-qr">
            <img src={qrPng} alt={`QR code for ${url}`} />
            <div className="hosted-actions">
              <a className="btn btn-secondary" href={qrPng} download={`quote-page-${widget.hostedCode}.png`}>
                <Download size={16} aria-hidden="true" /> PNG
              </a>
              <button type="button" className="btn btn-secondary" onClick={() => void downloadSvg()}>
                <Download size={16} aria-hidden="true" /> SVG (print)
              </button>
            </div>
          </div>
        )}
      </section>

      <div className="hosted-grid">
        <div className="hosted-col">
        {/* The page's look: logo, font and colours (default: the calculator's) */}
        <section className="card">
          <h2>Look</h2>
          <p className="card-sub">The header, page and footer. The calculator keeps its own look from the Branding tab.</p>
          <div className="hosted-form">
            <div className="hosted-field">
              <span className="hosted-label">Logo</span>
              <LogoUpload
                widgetId={widget.widgetId}
                value={d.logoUrl}
                onChange={(logoUrl) => setDraft({ ...d, logoUrl })}
                hint="PNG, JPEG or WebP, up to 1 MB. A wide logo about 400 × 120 px works best."
              />
            </div>
            <Toggle
              id="hosted-own-theme"
              checked={d.theme !== null}
              label="Use its own colours and font"
              onChange={(own) => setDraft({ ...d, theme: own ? hostedTheme(null, branding) : null })}
            />
            {d.theme ? (
              <>
                <div className="hosted-field">
                  <span className="hosted-label">Font</span>
                  <FontPicker value={d.theme.font} onChange={(font) => setDraft({ ...d, theme: { ...d.theme!, font } })} />
                </div>
                <div className="ed-colors">
                  {THEME_COLOURS.map(([key, label]) => (
                    <ColorField key={key} id={`hosted-${key}`} label={label} value={d.theme![key]} onChange={(v) => setDraft({ ...d, theme: { ...d.theme!, [key]: v } })} />
                  ))}
                </div>
              </>
            ) : (
              <p className="hosted-hint">Using the calculator's font and colours.</p>
            )}
          </div>
        </section>

        {/* Details form */}
        <section className="card">
          <div className="hosted-card-head">
            <h2>Header and footer</h2>
            {/* Fill any empty field from Account → Profile (never overwrites what's typed) */}
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => void fillFromProfile()} disabled={filling}>
              <UserRound size={14} aria-hidden="true" /> {filling ? "Filling…" : "Fill from profile"}
            </button>
          </div>
          <p className="card-sub">Your details around the calculator. Your logo and colours are under Look.</p>
          <div className="hosted-form">
            {FIELDS.map(([key, label, hint, type]) => (
              <label key={key} className="hosted-field">
                <span className="hosted-label">{label}</span>
                <input
                  className="input"
                  type={type}
                  value={d[key]}
                  placeholder={type === "url" ? "https://" : ""}
                  onChange={(e) => {
                    setDraft({ ...d, [key]: e.target.value });
                    setMessage(null);
                  }}
                />
                {hint && <span className="hosted-hint">{hint}</span>}
              </label>
            ))}
          </div>
        </section>
        </div>

        {/* Preview: the hosted page as customers see it, with the real calculator */}
        <section className="card hosted-preview-card">
          <h2>Preview</h2>
          <p className="card-sub">A desktop view of the page, scaled to fit. The calculator uses sample addresses and your prices.</p>
          <HostedPreview details={d} branding={branding} pricing={widget.pricing} product={widget.product} widgetId={widget.widgetId} />
        </section>
      </div>
    </div>
  );
}

// Colour settings for the page's own theme
const THEME_COLOURS: ["headerBackground" | "headerText" | "accent" | "pageBackground" | "footerBackground" | "footerText", string][] = [
  ["headerBackground", "Header background"],
  ["headerText", "Header text"],
  ["accent", "Accent (rule and call button)"],
  ["pageBackground", "Page background"],
  ["footerBackground", "Footer background"],
  ["footerText", "Footer text"],
];

// The live page's layout width; the preview is zoomed down to fit its column
const PAGE_WIDTH = 1280;

/**
 * The hosted page in miniature: sticky header, the REAL widget renderer (mock
 * api, this widget's branding and prices) and the footer at the bottom, in a
 * scrollable frame. CSS zoom (not transform) keeps scrolling and sticky working.
 */
export function HostedPreview({
  details: d,
  branding,
  pricing,
  height,
  step,
  product,
  widgetId,
}: {
  details: HostedDetails;
  branding: WidgetBranding;
  pricing?: WidgetPricing | null;
  product?: WidgetProduct; // solar: mount the solar widget (docs/12)
  widgetId?: string; // solar: roof, location and result come from the console API for this widget
  height?: number; // frame height, px (default: most of the window)
  step?: string; // show this calculator step (the wizard shows the Property page)
}) {
  const frameRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<WidgetHandle | null>(null);
  // null until measured: the page stays hidden so it never shows at the wrong size
  const [zoom, setZoom] = useState<number | null>(null);
  const [ready, setReady] = useState(false);
  const pricingRef = useRef(normalisePricing(pricing));
  pricingRef.current = normalisePricing(pricing);
  // Solar answers come from the signed-in console API (the engine never ships in public JS)
  const consoleApi = useApi();
  const api = useRef(
    createMockApi(() => pricingRef.current, widgetId ? { solar: solarPreviewBackend(consoleApi, widgetId) } : undefined),
  ).current;

  // Mount the calculator once; restyle when the branding changes
  useEffect(() => {
    if (!hostRef.current) return;
    // Solar hosted pages show the solar widget (data from the console API) and the preview maps key
    handleRef.current = mountWidget(hostRef.current, {
      branding,
      api,
      contained: true,
      product,
      mapsKey: import.meta.env.VITE_GOOGLE_MAPS_BROWSER_KEY || undefined,
    });
    // Show the preview once its step has loaded (no flash of the first step)
    if (step) void handleRef.current.goTo(step as Parameters<WidgetHandle["goTo"]>[0]).then(() => setReady(true));
    else setReady(true);
    return () => {
      handleRef.current?.destroy();
      handleRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => handleRef.current?.update(branding), [branding]);

  // Fit the page's width into the frame, measured before the first paint
  useLayoutEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const fit = () => setZoom(Math.min(1, (el.clientWidth - 2) / PAGE_WIDTH));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The page's own look (falls back to the calculator's branding), as on the live page
  const t = hostedTheme(d, branding);
  const logo = d.logoUrl ?? branding.logoUrl;
  useEffect(() => ensureGoogleFont(t.font), [t.font]);
  return (
    <div className="hp-frame" ref={frameRef} style={height ? { height } : undefined}>
      <div
        className="hp"
        style={{
          zoom: zoom ?? 1,
          opacity: zoom === null || !ready ? 0 : 1,
          transition: "opacity 150ms ease-out",
          width: PAGE_WIDTH,
          fontFamily: fontStack(t.font),
          ["--hp-primary" as string]: t.accent,
          ["--hp-head-bg" as string]: t.headerBackground,
          ["--hp-head-text" as string]: t.headerText,
          ["--hp-page" as string]: t.pageBackground,
          ["--hp-foot-bg" as string]: t.footerBackground,
          ["--hp-foot-text" as string]: t.footerText,
        }}
      >
        <header className="hp-head">
          <div className="hp-brand">
            {logo ? <img src={logo} alt="" className="hp-logo" /> : null}
            <div>
              <strong>{d.companyName || "Your company"}</strong>
              {d.tagline && <span>{d.tagline}</span>}
            </div>
          </div>
          <div className="hp-contact">
            {d.phone && <span className="hp-btn hp-btn-primary">{d.phone}</span>}
            {d.email && <span className="hp-btn">{d.email}</span>}
          </div>
        </header>
        <main className="hp-body">
          <div ref={hostRef} />
        </main>
        <footer className="hp-foot">
          <div>
            <strong>{d.companyName || "Your company"}</strong>
            {d.address && <span>{d.address}</span>}
          </div>
          <div>
            {d.website && <span className="hp-link">{d.website.replace(/^https:\/\//, "").replace(/\/$/, "")}</span>}
            {d.privacyUrl && <span className="hp-link">Privacy policy</span>}
            {d.companyNumber && <span>Company number {d.companyNumber}</span>}
            {d.vatNumber && <span>VAT {d.vatNumber}</span>}
          </div>
        </footer>
      </div>
    </div>
  );
}
